import { Stack } from "@mui/material";
import type { ContactDetails } from "../../../../../api/types/fundStructureTypes";
import type { ObjectClassDefinition, ObjectPermissions } from "../../../../../api/types/objectTypes";
import useClientContext from "../../../../../context/useClientContext";
import ScrollableColumn from "../../../../common/layout/ScrollableColumn";
import ScrollableColumns from "../../../../common/layout/ScrollableColumns";
import CreatedAndModifiedTimestamps from "../../../../entityFields/CreatedAndModifiedTimestamps";
import EntityNotesSection from "../../../../entityFields/EntityNotesSection";
import { getNotesEntityType } from "../../../../entityFields/entityTypesHelper";
import { NotesContextProvider } from "../../../../notes/NotesContextProvider";
import ContactCard from "./ContactCard";
import ContactPortalAccessSection from "./portal-access/ContactPortalAccessSection";

interface Props {
  contactDetails: ContactDetails;
  objectDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  onContactDetailsChange: (details: Partial<ContactDetails>) => void;
  onUpdateContactDetails: () => void;
  onManageAccess: () => void;
  onViewEmails: () => void;
  onViewDocumentActivity: () => void;
}

const ContactDetailsMainTab = ({
  contactDetails,
  objectDefinition,
  objectPermissions,
  onContactDetailsChange,
  onUpdateContactDetails,
  onManageAccess,
  onViewEmails,
  onViewDocumentActivity,
}: Props) => {
  const { hasAnyPermission } = useClientContext();

  const hasPermissionsToManageFields = hasAnyPermission(objectPermissions.objectDataWritePermissions);
  const hasPermissionsToViewNotes = hasAnyPermission(objectPermissions.notesReadPermissions);
  const hasPermissionsToManageNotes = hasAnyPermission(objectPermissions.notesWritePermissions);

  return (
    <ScrollableColumns gridProps={{ columnSpacing: 2.5 }}>
      <ScrollableColumn>
        <ContactCard
          key={contactDetails.contact.email}
          contact={contactDetails.contact}
          objectDefinition={objectDefinition}
          onContactDetailsChange={onContactDetailsChange}
          onUpdateContactDetails={onUpdateContactDetails}
          hasPermissionsToManageFields={hasPermissionsToManageFields}
        />
        <CreatedAndModifiedTimestamps
          stackProps={{ sx: { mt: 2.5 } }}
          createdAt={contactDetails.createdAt}
          modifiedAt={contactDetails.updateAt}
        />
      </ScrollableColumn>
      <ScrollableColumn>
        <Stack spacing={2.5}>
          <ContactPortalAccessSection
            contact={contactDetails.contact}
            permissions={contactDetails.permissions}
            hasPermissionsToManage={hasPermissionsToManageFields}
            onManageAccess={onManageAccess}
            onViewEmails={onViewEmails}
            onViewDocumentActivity={onViewDocumentActivity}
            onUpdateContactDetails={onUpdateContactDetails}
          />
          {hasPermissionsToViewNotes && (
            <NotesContextProvider
              entityType={getNotesEntityType(objectDefinition.objectType)}
              entityId={contactDetails.contact.id}
              hasPermissionsToManageNotes={hasPermissionsToManageNotes}
            >
              <EntityNotesSection />
            </NotesContextProvider>
          )}
        </Stack>
      </ScrollableColumn>
    </ScrollableColumns>
  );
};

export default ContactDetailsMainTab;
